import React, { createContext, useState, useEffect, useContext } from 'react';
import { trackAddToCart } from '../utils/analytics';

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);

  // Cart persistence functions
  const CART_STORAGE_KEY = 'create3d_cart';
  const CART_EXPIRY_DAYS = 7;

  const saveCartToStorage = (cartItems) => {
    try {
      const cartData = {
        items: cartItems,
        timestamp: new Date().getTime()
      };
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartData));
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  };

  const loadCartFromStorage = () => {
    try {
      const savedCart = localStorage.getItem(CART_STORAGE_KEY);
      if (!savedCart) return [];

      const cartData = JSON.parse(savedCart);
      const currentTime = new Date().getTime();
      const expiryTime = CART_EXPIRY_DAYS * 24 * 60 * 60 * 1000;

      if (currentTime - cartData.timestamp > expiryTime) {
        localStorage.removeItem(CART_STORAGE_KEY);
        return [];
      }

      return cartData.items || [];
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
      return [];
    }
  };

  // Load cart from storage on component mount
  useEffect(() => {
    const savedCartItems = loadCartFromStorage();
    if (savedCartItems.length > 0) {
      setCart(savedCartItems);
      console.log(`Restored ${savedCartItems.length} items from saved cart`);
    }
  }, []);

  // Save cart to storage whenever cart changes
  useEffect(() => {
    if (cart.length > 0) {
      saveCartToStorage(cart);
    } else {
      localStorage.removeItem(CART_STORAGE_KEY);
    }
  }, [cart]);

  const addToCart = (product) => {
    const existingItem = cart.find(item => item.id === product.id);
    if (existingItem) {
      setCart(cart.map(item => 
        item.id === product.id 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, { ...product, quantity: 1 }]);
      
      trackAddToCart({
        id: product.id,
        name: product.title || product.name,
        price: product.price,
        type: product.type || 'Product'
      });
    }
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
    } else {
      setCart(cart.map(item => 
        item.id === productId 
          ? { ...item, quantity }
          : item
      ));
    }
  };

  const clearCart = () => {
    setCart([]);
    localStorage.removeItem(CART_STORAGE_KEY);
  };

  return (
    <CartContext.Provider value={{
      cart,
      setCart,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
